import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Appointments from './Appointments';
import UserDetail from './UserDetail';
import Users from './Users';
import AskDoctor from './AskDoctor';
import AnswerQuestion from './AnswerQuestion';
import Offers from './Offers';
import CreateOffer from './CreateOffer';
import EditOffer from './EditOffer';
import Login from './Login';
import Sidebar from './Sidebar';
import DeleteUserByEmail from './DeleteUserByEmail.jsx';
import PrivacyPolicy from './PrivacyPolicy.jsx';
import './App.css';
import './Users.css';
import './Appointments.css';

// Layout component that conditionally renders the admin layout
const AdminLayout = ({ children }) => {
  const location = useLocation();
  
  // Check if current path is the delete user page
  if (location.pathname === '/deleteUserByEmail') {
    // Return only the children without admin layout
    return <>{children}</>;
  }
  if (location.pathname === '/privacyPolicy') {
    // Return only the children without admin layout
    return <>{children}</>;
  }
  
  // Return normal admin layout for all other pages
  return (
    <div className="admin-container">
      {/* Header */}
      <header className="admin-header">
        <h1>Admin Panel</h1>
        <div className="user-controls">
          <span className="user-info">
            {JSON.parse(localStorage.getItem('user') || '{}').name || 'Admin'} 
            ({JSON.parse(localStorage.getItem('user') || '{}').role || 'Admin'})
          </span>
          <button className="logout-button" onClick={() => {
            localStorage.removeItem('authToken');
            localStorage.removeItem('user');
            window.location.href = '/login';
          }}>
            Logout
          </button>
        </div>
      </header>

      {/* Content area with sidebar and main content */}
      <div className="admin-panel">
        <div className="main-content">
          {/* Sidebar */}
          <Sidebar />
          
          {/* Main content area */}
          <main className="content">
            {children}
          </main>
        </div>
      </div>
    </div>
  );
};

const App = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const checkAuth = () => {
      const token = localStorage.getItem('authToken');
      if (token) {
        setIsAuthenticated(true);
      }
      setIsLoading(false);
    };
    
    checkAuth();
  }, []);

  const handleLogin = () => {
    setIsAuthenticated(true);
  };

  if (isLoading) {
    return <div className="loading-screen">Loading...</div>;
  }

  return (
    <BrowserRouter>
      <div className="app">
        <Routes>
          {/* Public routes that don't require authentication */}
          <Route path="/deleteUserByEmail" element={<DeleteUserByEmail />} />
          <Route path="/privacyPolicy" element={<PrivacyPolicy />} />

          {/* Protected routes that require authentication */}
          {isAuthenticated ? (
            <>
              <Route path="/" element={
                <AdminLayout>
                  <Navigate to="/appointments" />
                </AdminLayout>
              } />
              <Route path="/appointments" element={
                <AdminLayout>
                  <Appointments />
                </AdminLayout>
              } />
              <Route path="/users" element={
                <AdminLayout>
                  <Users />
                </AdminLayout>
              } />
              <Route path="/askDoctor" element={
                <AdminLayout>
                  <AskDoctor />
                </AdminLayout>
              } />
              <Route path="/user/:userId" element={
                <AdminLayout>
                  <UserDetail />
                </AdminLayout>
              } />
              <Route path="/question/:id" element={
                <AdminLayout>
                  <AnswerQuestion />
                </AdminLayout>
              } />
              <Route path="/offers" element={
                <AdminLayout>
                  <Offers />
                </AdminLayout>
              } />
              <Route path="/createOffer" element={
                <AdminLayout>
                  <CreateOffer />
                </AdminLayout>
              } />
              <Route path="/editOffer/:id" element={
                <AdminLayout>
                  <EditOffer />
                </AdminLayout>
              } />
              <Route path="*" element={
                <AdminLayout>
                  <Navigate to="/appointments" />
                </AdminLayout>
              } />
            </>
          ) : (
            // If not authenticated, show login for all protected routes
            <Route path="*" element={<Login onLogin={handleLogin} />} />
          )}
        </Routes>
      </div>
    </BrowserRouter>
  );
};

export default App;